import { api } from '@goaltree/backend/convex/_generated/api'
import { useMutation, useQuery } from 'convex/react'
import { useEffect } from 'react'

import { breatherDayKeys, isOnBreather, todayKey } from '@/lib/day'
import { patchPrefs } from '@/lib/prefs-optimistic'

type BreatherPrefs = {
  breatherFrom?: string
  breatherUntil?: string
  breatherReturnPending?: boolean
}

// "Take a breather" (away/pause) state + controls. Reads the same preferences
// the settings page writes; `onBreather` reflects today falling inside the
// window, `returnPending` drives the one-time welcome-back.
export function useBreather({ autoEnd = false }: { autoEnd?: boolean } = {}) {
  const prefs = useQuery(api.preferences.getMyPreferences) as BreatherPrefs | undefined

  // Optimistic so the switch flips instantly instead of waiting on the round-trip.
  const start = useMutation(api.preferences.startBreather).withOptimisticUpdate(
    (localStore, args) => {
      const cur = localStore.getQuery(api.preferences.getMyPreferences, {})
      if (cur === undefined) return
      localStore.setQuery(api.preferences.getMyPreferences, {}, {
        ...cur,
        breatherFrom: args.from ?? todayKey(),
        breatherUntil: args.until,
        breatherReturnPending: false,
      } as NonNullable<typeof cur>)
    },
  )
  const end = useMutation(api.preferences.endBreather).withOptimisticUpdate((localStore) => {
    const cur = localStore.getQuery(api.preferences.getMyPreferences, {})
    if (cur === undefined) return
    localStore.setQuery(api.preferences.getMyPreferences, {}, {
      ...cur,
      breatherFrom: undefined,
      breatherUntil: undefined,
      breatherReturnPending: !!(cur as BreatherPrefs).breatherFrom,
    } as NonNullable<typeof cur>)
  })
  const dismissReturn = useMutation(api.preferences.dismissBreatherReturn).withOptimisticUpdate(
    (localStore) => patchPrefs(localStore, { breatherReturnPending: false }),
  )

  const from = prefs?.breatherFrom
  const until = prefs?.breatherUntil

  // A scheduled breather whose end date has passed auto-ends — clears the
  // window and flags the one-time welcome-back. Only the caller that opts in
  // (the Today route) runs this, so it fires once rather than per dot-grid.
  useEffect(() => {
    if (autoEnd && from && until && todayKey() > until) void end({}).catch(() => {})
  }, [autoEnd, from, until, end])

  return {
    loading: prefs === undefined,
    from,
    until,
    onBreather: isOnBreather(from, until),
    returnPending: !!prefs?.breatherReturnPending,
    pausedDays: () => new Set(breatherDayKeys(from, until)),
    start,
    end,
    dismissReturn,
  }
}
